<script setup lang="ts">
import { vMaska } from 'maska';

type Props = {
  type?: string;
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  minlength?: number;
  maxlength?: number;
  compact?: boolean;
  icon?: boolean;
  initialValue?: string;
  dataMaska?: string | string[] | ((val: string) => string);
  dataMaskaEager?: boolean;
  name?: string;
  suffix?: string;
  prefix?: string;
  ddi?: boolean;
  showHidePass?: boolean;
  error?: string;
  info?: string;
  withCheck?: boolean;
  hasError?: boolean;
};

const props = defineProps<Props>();

const slots = useSlots();

const hasPrepend = computed<boolean>(() => {
  return !!props.prefix || !!slots.prepend;
});

const isPassword = ref<boolean>(true);

const isFieldType = computed(() => {
  // Se showHidePass estiver habilitado, permitir alternar entre password/text
  if (props.showHidePass) {
    return isPassword.value ? 'password' : 'text';
  }

  // Caso contrário, respeitar o tipo informado ou usar 'text' como padrão
  if (props.type) {
    return props.type;
  }

  return 'text';
});

const maskaOptions = computed<Record<string, any>>(() => {
  // aceita prop dataMaska/dataMaskaEager OU atributos data-maska/data-maska-eager
  const a = useAttrs() as Record<string, any>;
  const maskVal = (props.dataMaska ?? a?.['data-maska']) as
    | string
    | string[]
    | ((val: string) => string)
    | undefined;

  const eagerVal = (props.dataMaskaEager ?? a?.['data-maska-eager']) as
    | boolean
    | undefined;

  const options: Record<string, any> = {};

  if (maskVal !== undefined) options.mask = maskVal;
  if (eagerVal !== undefined) options.eager = eagerVal;

  return options;
});

const hasMask = computed(() => {
  const a = useAttrs() as Record<string, any>;
  const m = (props.dataMaska ?? a?.['data-maska']) as unknown;

  if (Array.isArray(m)) return m.length > 0;
  if (typeof m === 'function') return true;

  return typeof m === 'string' && m.length > 0;
});

const modelValue = defineModel<string>('modelValue', {
  default: '',
});

const hasSuffix = computed(() => {
  return (
    !!props.suffix || !!props.showHidePass || !!props.info || !!props.withCheck
  );
});

// Evitar que data-maska* vá para o DOM e sobrescreva as opções do v-maska
const attrs = useAttrs();
const forwardedAttrs = computed<Record<string, any>>(() => {
  const {
    'data-maska': _dm,
    'data-maska-eager': _dme,
    dataMaska: _dmc,
    dataMaskaEager: _dmec,
    ...rest
  } = (attrs as unknown as Record<string, any>) || {};

  return rest;
});

watch(
  () => props.initialValue,
  () => (modelValue.value = props.initialValue || ''),
  {
    immediate: false,
  },
);

onMounted(() => {
  if (props.initialValue !== undefined) {
    modelValue.value = props.initialValue;
  }
});
</script>

<template>
  <div
    :class="[
      'input-group',
      { 'is-disabled': props.disabled },
      { 'has-error': props.hasError },
    ]"
  >
    <div
      v-if="hasPrepend"
      class="prepend"
    >
      <slot name="prepend">
        <span
          v-if="props.prefix"
          class="prefix"
        >
          {{ props.prefix }}
        </span>
      </slot>
    </div>

    <div
      v-if="props.ddi"
      class="prefix ddi"
    >
      <slot name="ddi"></slot>
    </div>

    <component
      :is="props.label ? 'label' : 'div'"
      class="group"
      :class="{
        icon: props.icon,
        suffix: props.suffix,
        hasSuffix: hasSuffix,
        prefix: hasPrepend,
        ddi: props.ddi,
        hasLabel: props.label,
        isCompact: props.compact,
        placeh: 'placeholding',
      }"
      :disabled="props.disabled"
      :for="props.id || 'id'"
      :readonly="props.readonly"
    >
      <input
        v-if="hasMask"
        :id="props.id || 'id'"
        v-model="modelValue"
        v-maska:[maskaOptions]
        class="peer input"
        :class="[
          { padRight: props.showHidePass },
          { hasContent: modelValue },
          { 'has-error': props.hasError },
        ]"
        :disabled="props.disabled"
        :maxlength="props.maxlength"
        :minlength="props.minlength"
        :name="props.name || 'input-name'"
        :placeholder="props.placeholder || ''"
        :readonly="props.readonly"
        :type="isFieldType"
        :validate-on-blur="true"
        :validate-on-change="true"
        v-bind="forwardedAttrs"
      />

      <input
        v-else
        :id="props.id || 'id'"
        v-model="modelValue"
        class="peer input"
        :class="[
          { padRight: props.showHidePass },
          { hasContent: modelValue },
          { 'has-error': props.hasError },
        ]"
        :disabled="props.disabled"
        :maxlength="props.maxlength"
        :minlength="props.minlength"
        :name="props.name || 'input-name'"
        :placeholder="props.placeholder || ''"
        :readonly="props.readonly"
        :type="isFieldType"
        :validate-on-blur="true"
        :validate-on-change="true"
        v-bind="forwardedAttrs"
      />

      <span
        v-if="props.label"
        class="label"
      >
        {{ props.label || 'Label' }}
        <small
          v-if="props.required"
          class="required"
          >*</small
        >
      </span>
      <div
        v-if="props.showHidePass"
        class="suffix-icon cursor-pointer mx-4"
        @click="isPassword = !isPassword"
      >
        <NuxtIcon
          v-if="isPassword"
          name="eye_slash"
        />
        <NuxtIcon
          v-else
          name="eye"
        />
      </div>
      <div
        v-else-if="props.info"
        class="suffix-icon mx-0"
      >
        <div
          v-tooltip.bottom.padding.left="props.info"
          class="infoSection"
        >
          <NuxtIcon name="info" />
        </div>
      </div>
      <div
        v-else-if="props.withCheck"
        class="suffix-icon cursor-default mx-4 text-success"
      >
        <NuxtIcon name="check" />
      </div>
    </component>

    <span
      v-if="props.suffix"
      class="sufix"
    >
      {{ props.suffix }}
    </span>

    <div
      v-if="props.error"
      class="errorMessage"
    >
      {{ props.error }}
    </div>
  </div>
</template>

<style scoped lang="postcss">
.input-group {
  @apply flex flex-wrap border-none rounded-md relative bg-auth-bg-inputs;

  &.has-error {
    box-shadow: 0 0 200px inset rgba(255, 0, 0, 0.1);
  }

  &.is-disabled {
    @apply cursor-not-allowed opacity-60;
  }
}

.prepend {
  @apply grid place-items-center rounded-l-[inherit];
}

.prefix,
.suffix {
  @apply w-[60px] flex justify-center items-center sm:text-sm px-2 text-auth-text-inputs/50 overflow-hidden text-ellipsis;
}
.prefix {
  @apply rounded-l-[inherit];

  &.ddi {
    @apply w-[90px] bg-auth-bg-inputs text-auth-text-inputs/50 border-solid border-r border-auth-text-inputs/20;
  }
}

.ddi {
  @apply w-[95px] bg-auth-bg-inputs text-auth-text-inputs border-solid border-r border-auth-text-inputs/20 flex items-center rounded-l-[inherit];
}

.suffix {
  @apply rounded-r-md;

  .ddi {
    @apply w-[95px] bg-auth-bg-inputs text-auth-text-inputs border-solid border-l border-auth-text-inputs/20;
  }
}

.group {
  @apply relative flex items-center overflow-hidden rounded-md px-4 h-[46px] sm:h-[50px] flex-grow cursor-text border-solid border-0 border-auth-bg-inputs;

  &.hasLabel {
    @apply pt-3;
  }

  &.isCompact {
    @apply h-9 text-sm text-left px-0;
  }

  .input::placeholder {
    @apply text-sm text-auth-text-inputs opacity-70 md:text-[.9rem];
  }

  &:not(.hasLabel) {
    .input::placeholder {
      @apply !text-auth-text-inputs/50;
    }
  }

  .suffix-icon {
    @apply flex justify-center items-center absolute top-0 bottom-0 right-0 text-texts;
  }

  .infoSection {
    @apply flex justify-center items-center h-[46px] sm:h-[50px] w-[46px] sm:w-[50px] m-0 cursor-pointer;
  }
}

.group.prefix {
  @apply rounded-l-none;
}
.group.suffix,
.group.ddi {
  @apply rounded-l-none max-w-[calc(100%-60px)];
}
.icon {
  @apply pl-[46px] !important;
  @apply sm:pl-[50px] !important;
}
.input {
  @apply h-8 w-full text-sm border-none bg-transparent p-0 placeholder-transparent focus:border-transparent focus:outline-none focus:ring-0 shadow-input shadow-transparent text-auth-text-inputs autofill:shadow-auth-bg-inputs autofill:text-auth-text-inputs data-[com-onepassword-filled]:bg-clip-text !important;
  border-radius: inherit;
  background-clip: padding-box;

  &.has-error {
    box-shadow: 0 0 200px inset rgba(255, 0, 0, 0.1);
  }

  &.padRight {
    padding-right: 30px !important;
  }
}

.hasSuffix {
  .input {
    @apply pr-[36px] !important;
    @apply sm:pr-[40px] !important;
  }
}

.label {
  @apply absolute start-4 top-1/2 -translate-y-1/2 text-sm text-auth-text-inputs opacity-70 cursor-text select-none leading-5 peer-[.hasContent]:top-[.8rem] peer-[.hasContent]:text-[.52rem] peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-sm peer-focus:top-[.8rem] peer-focus:text-[.52rem] peer-focus:opacity-70 pointer-events-none;
}
.icon .label {
  @apply !start-[46px] sm:!start-[50px];
}

.group[disabled]:not([disabled='false']),
.input[disabled]:not([disabled='false']),
.group[disabled]:not([disabled='false']) .label,
.group[readonly]:not([readonly='false']),
.input[readonly]:not([readonly='false']),
.group[readonly]:not([readonly='false']) .label {
  @apply cursor-default opacity-70;
}

.required {
  @apply font-bold text-opacity-70 text-sm text-error;
}

.errorMessage {
  @apply absolute bottom-0 right-0 rounded-sm rounded-br text-center text-[.5rem] pr-1 pl-1.5 pt-1 pb-0.5 bg-error text-texts;
}

/* Autofill normalization to avoid grey background and keep consistent with container */
.input:-webkit-autofill,
.input:-webkit-autofill:hover,
.input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px var(--at-input-bg, #0c1b44) inset !important;
  -webkit-text-fill-color: var(--at-input-fg, #cbd5e1) !important;
  caret-color: var(--at-input-fg, #cbd5e1) !important;
  transition: background-color 9999s ease-out 0s !important;
  border-radius: inherit;
}

/* Text selection color inside input */
.input::selection {
  background-color: rgba(0, 139, 255, 0.35);
  color: inherit;
}
</style>
